import { useEffect, useState } from "react";
import { TrendingUp, Wallet, Video, MapPin, AlertTriangle, Stethoscope, Users as UsersIcon } from "lucide-react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

const MONTH_LABELS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

function fmtMonth(m) {
  const [y, mo] = (m || "").split("-");
  return MONTH_LABELS[(parseInt(mo, 10) || 1) - 1] + " " + (y || "").slice(2);
}

function StatCard({ icon: Icon, label, value, subtitle, color = "emerald" }) {
  const palette = {
    emerald: "from-emerald-50 to-white border-emerald-100 text-emerald-700",
    blue: "from-blue-50 to-white border-blue-100 text-blue-700",
    amber: "from-amber-50 to-white border-amber-100 text-amber-700",
    rose: "from-rose-50 to-white border-rose-100 text-rose-700",
    violet: "from-violet-50 to-white border-violet-100 text-violet-700",
  }[color];
  return (
    <Card className={`bg-gradient-to-br ${palette} card-lift`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
            <div className="font-display text-3xl mt-1 text-foreground">{value}</div>
            {subtitle && <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>}
          </div>
          <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center">
            <Icon className="w-5 h-5" strokeWidth={1.6} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MonthlyBars({ series }) {
  const max = Math.max(1, ...series.map((s) => s.count));
  return (
    <div className="space-y-3">
      {series.map((s) => {
        const pct = (s.count / max) * 100;
        const videoPct = s.count ? (s.video / s.count) * 100 : 0;
        return (
          <div key={s.month} data-testid={`bar-${s.month}`}>
            <div className="flex justify-between text-xs mb-1">
              <span className="font-medium text-foreground">{fmtMonth(s.month)}</span>
              <span className="text-muted-foreground">{s.count} citas · {s.revenue}€</span>
            </div>
            <div className="h-6 rounded-md bg-slate-100 overflow-hidden relative">
              <div
                className="h-full flex transition-all"
                style={{ width: `${pct}%` }}
              >
                <div className="bg-blue-500" style={{ width: `${videoPct}%` }} title={`${s.video} video`} />
                <div className="bg-emerald-600 flex-1" title={`${s.in_person} presencial`} />
              </div>
            </div>
          </div>
        );
      })}
      <div className="flex gap-4 text-xs pt-2 border-t">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-500" /> Video</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-600" /> Presencial</span>
      </div>
    </div>
  );
}

export default function DoctorMetrics() {
  const [m, setM] = useState(null);
  useEffect(() => {
    api.get("/doctor/metrics").then(({ data }) => setM(data)).catch(() => {});
  }, []);

  if (!m) return <div className="text-sm text-muted-foreground">Cargando métricas...</div>;

  const tm = m.this_month || {};
  return (
    <div className="space-y-6" data-testid="doctor-metrics">
      <div>
        <h2 className="font-display text-2xl flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-emerald-700" />
          Panel de gestión
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Resumen del mes y tendencias de tu consulta</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Wallet} label="Ingresos del mes" value={`${tm.revenue}€`} subtitle={`${tm.appointments} citas en ${fmtMonth(tm.month)}`} color="emerald" />
        <StatCard icon={Video} label="Videoconsultas" value={tm.video} subtitle="este mes" color="blue" />
        <StatCard icon={MapPin} label="Presenciales" value={tm.in_person} subtitle="este mes" color="violet" />
        <StatCard icon={AlertTriangle} label="Pagos pendientes" value={tm.pending_payments} subtitle="por cobrar" color="amber" />
        <StatCard icon={TrendingUp} label="Cancelación" value={`${m.no_show_rate}%`} subtitle="histórico" color="rose" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-700" /> Últimos 6 meses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MonthlyBars series={m.series_6m || []} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-emerald-700" /> Top diagnósticos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5">
            {(m.top_diagnoses || []).map((d, i) => (
              <div key={i} className="flex items-center justify-between text-sm" data-testid={`top-diag-${i}`}>
                <span className="truncate flex-1 text-foreground">{d.label}</span>
                <span className="font-mono text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 ml-2">{d.count}</span>
              </div>
            ))}
            {(m.top_diagnoses || []).length === 0 && <div className="text-xs text-muted-foreground">Sin datos aún</div>}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <UsersIcon className="w-5 h-5 text-emerald-700" /> Pacientes más frecuentes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(m.top_patients || []).map((p, i) => (
            <div key={p.patient_id} className="flex items-center justify-between py-2 border-b last:border-0" data-testid={`top-patient-${i}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {(p.name || "?").charAt(0)}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{p.name}</div>
                  {p.email && <div className="text-xs text-muted-foreground truncate">{p.email}</div>}
                </div>
              </div>
              <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">{p.count} visita(s)</span>
            </div>
          ))}
          {(m.top_patients || []).length === 0 && <div className="text-xs text-muted-foreground">Sin datos aún</div>}
        </CardContent>
      </Card>
    </div>
  );
}
